import { initializeApp } from 'firebase/app'
import {getMessaging, onMessage, isSupported } from 'firebase/messaging'
import type { Messaging } from 'firebase/messaging'
import type { FirebaseOptions } from "@firebase/app";
import {useNotificationsStore} from "~/stores/NotificationsStore";

// Provided imperatively via nuxtApp.provide() below, so declare the injected
// types here (they can no longer be inferred from a returned `provide` object).
declare module '#app' {
  interface NuxtApp {
    $messaging: Messaging | null;
    $isSupportedBrowser: boolean;
  }
}

export default defineNuxtPlugin(async () => {
  const nuxtApp = useNuxtApp();
  const runtimeConfig = useRuntimeConfig();
  const { isOnline } = useConnectivity();

  let isSupportedBrowser = false;
  let messaging: Messaging | null = null;

  try {
    const wadFirebaseApp = initializeApp({
      apiKey: runtimeConfig.public.firebaseApiKey,
      authDomain: runtimeConfig.public.firebaseAuthDomain,
      projectId: runtimeConfig.public.firebaseProjectId,
      storageBucket: runtimeConfig.public.firebaseStorageBucket,
      messagingSenderId: runtimeConfig.public.firebaseMessagingSenderId,
      appId: runtimeConfig.public.firebaseAppId,
    } as FirebaseOptions);

    isSupportedBrowser = await isSupported();

    if(isSupportedBrowser) {
      try {
        messaging = getMessaging(wadFirebaseApp);
      } catch (error) {
        console.warn("Firebase Messaging initialization failed:", error);
      }
    }
  } catch (error) {
    // Push notifications are non-essential — never let init failure block the rest of the app.
    nuxtApp.$bugsnag?.notify(error as Error);
  }

  // Provide imperatively (rather than via a returned `provide`) so composables can
  // resolve $messaging even when the immediate watcher below fires during plugin
  // setup, before the plugin returns. Otherwise getToken($messaging, ...) gets undefined.
  nuxtApp.provide('messaging', messaging);
  nuxtApp.provide('isSupportedBrowser', isSupportedBrowser);

  if(messaging) {
    // onMessage fires when a push arrives while the app tab is foregrounded.
    // We forward to the service worker so it shows the OS notification via showNotification()
    // Fallback: if the SW has no active worker (e.g. during an update cycle), use the
    // Notification API directly so the push is never silently dropped.
    onMessage(messaging as Messaging, async (payload) => {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration?.active) {
        registration.active.postMessage({
          type: 'SHOW_NOTIFICATION',
          payload
        });
      } else if (Notification.permission === 'granted') {
        new Notification(payload.data?.wad_title || 'Notification', {
          body: payload.data?.wad_message,
          icon: 'notifications/icon.png',
        });
      }
    });

    const {data: userData} = useAuth();
    const {userLoggedIn} = useWadAuth();
    const notificationsStore = useNotificationsStore();
    const {newTokenRequested, notifyOtherDevices} = storeToRefs(notificationsStore);
    const {setToken, optOutFromAll} = useFirebaseNotification();

    watch(
        () => userLoggedIn.value,
        async (loggedIn) => {
          if (!loggedIn) {
            // Clear local token on logout to prevent stale "on" state if a different
            // user logs in on this device. newTokenRequested reset allows a fresh
            // permission prompt for the next user.
            newTokenRequested.value = false;
            notificationsStore.clearFcmToken();
            return;
          }

          const userHasEnabledNotifications = userData.value?.data.app_notifications_enabled;

          if (!userHasEnabledNotifications) {
            // Server says off (e.g. another device disabled and wiped all tokens).
            // Clear stale local state so the toggle reflects reality.
            notificationsStore.clearFcmToken();
            newTokenRequested.value = false;
            return;
          }

          if (!isOnline.value) return;

          if (Notification.permission === 'granted') {
            // Permission already granted — always re-register. storeFcmToken is idempotent
            // so this is safe on every login. Ensures the token is in the DB even after
            // another device wiped all tokens via a global disable.
            try {
              await setToken(false);
            } catch (err) {
              console.error('Failed to re-register FCM token', err);
            }
            return;
          }

          // Permission not yet asked — prompt the browser once per device.
          if (!newTokenRequested.value) {
            newTokenRequested.value = true;
            try {
              const permission = await Notification.requestPermission();
              if (permission === 'granted') {
                await setToken(false);
              }
            } catch (err) {
              console.error('Failed to get FCM token', err);
              newTokenRequested.value = false;
            }
          }
        },
        { immediate: true }
    );

    // Cross-device sync: another device disabled notifications (server wiped all tokens).
    // Clear local state so the toggle shows OFF without an extra API call.
    window.addEventListener('wad:push-notifications-disabled', async () => {
      // This device triggered the change: the SSE is our own echo. Consume the flag
      // and stop — re-running the opt-out here would loop back through the server.
      if (notifyOtherDevices.value) {
        notifyOtherDevices.value = false;
        return;
      }
      await optOutFromAll(false);
    });

    // Cross-device sync: another device enabled notifications.
    // Re-register this device's token if browser permission is already granted.
    window.addEventListener('wad:push-notifications-enabled', async () => {
      // This device triggered the change: the SSE is our own echo. Consume the flag
      // and stop. Re-registering here would POST with notify_other_devices still true,
      // the server would re-broadcast, and we'd loop. A genuine remote enable arrives
      // with notifyOtherDevices=false, so setToken below won't re-broadcast.
      if (notifyOtherDevices.value) {
        notifyOtherDevices.value = false;
        return;
      }
      if (Notification.permission === 'granted' && isOnline.value) {
        try {
          await setToken(false);
        } catch (err) {
          console.error('Failed to re-register FCM token after remote enable', err);
        }
      }
    });
  }
});
